// src/features/analytics/UserActivityDetail.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
// Подключаем наш настроенный API клиент
import API from '../../api/axiosConfig';

const UserActivityDetail = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('logs'); // 'logs' или 'sessions'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [auditData, setAuditData] = useState({ logs: [], sessions: [] });

  const loadAuditData = useCallback(() => {
    setLoading(true);
    // Используем API вместо axios, без хардкода IP и ручного указания withCredentials
    API.get(`/api/users/${username}/audit/`)
    .then(res => {
      setAuditData({
        logs: res.data.logs || [],
        sessions: res.data.sessions || []
      });
      setLoading(false);
    })
    .catch(err => {
      console.error("Ошибка загрузки аудита:", err);
      setError(err.response?.data?.error || 'Не удалось загрузить данные аудита безопасности');
      setLoading(false);
    });
  }, [username]);

  useEffect(() => {
    loadAuditData();
  }, [loadAuditData]);

  // Быстрый парсинг URL-параметров для переключения вкладок (например, если пришли по кнопке IP)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('tab') === 'sessions') {
      setActiveTab('sessions');
    }
  }, []);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="font-black text-gray-400 uppercase tracking-widest text-xs">Анализ логов безопасности...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F3F4F6] p-4 md:p-8 font-sans">
      <div className="max-w-7xl mx-auto">
        
        {/* Кнопка Назад и Шапка */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <button 
              onClick={() => navigate('/analytics')} 
              className="text-xs font-black text-blue-600 uppercase tracking-wider hover:underline mb-2 block"
            >
              ← Вернуться к общей аналитике
            </button>
            <h1 className="text-3xl font-black text-gray-900 uppercase tracking-tight">
              Аудит безопасности: <span className="text-blue-600">{username}</span>
            </h1>
          </div>

          {/* Переключатель вкладок в стиле твоих кнопок */}
          <div className="flex bg-white p-1.5 rounded-2xl border border-gray-100 shadow-sm self-start">
            <button 
              onClick={() => setActiveTab('logs')}
              className={`px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all ${activeTab === 'logs' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-500 hover:text-gray-900'}`}
            >
              📋 Логи действий
            </button>
            <button 
              onClick={() => setActiveTab('sessions')}
              className={`px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all ${activeTab === 'sessions' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-500 hover:text-gray-900'}`}
            >
              🔑 Сессии и IP
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-100 text-red-600 p-4 rounded-2xl text-sm font-bold mb-6">
            ⚠️ {error}
          </div>
        )}

        {/* Контент вкладок */}
        <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden">
          
          {activeTab === 'logs' ? (
            <div>
              <div className="p-6 border-b border-gray-50 bg-gray-50/50">
                <h3 className="font-black text-xs text-gray-400 uppercase tracking-widest">Хронология активности на платформе</h3>
              </div>
              <div className="overflow-auto max-h-[600px]">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-gray-50/70 text-gray-400 text-[10px] font-black uppercase tracking-wider border-b border-gray-100">
                      <th className="px-6 py-4">Время события</th>
                      <th className="px-6 py-4">Действие / Эндпоинт</th>
                      <th className="px-6 py-4">IP-Адрес</th>
                      <th className="px-6 py-4">Статус</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {auditData.logs.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="p-10 text-center text-sm font-medium text-gray-400">Действий пользователя не зафиксировано</td>
                      </tr>
                    ) : (
                      auditData.logs.map((log, idx) => (
                        <tr key={idx} className="hover:bg-gray-50/50 transition-colors text-sm">
                          <td className="px-6 py-4 text-gray-500 font-medium">
                            {new Date(log.timestamp).toLocaleString()}
                          </td>
                          <td className="px-6 py-4 font-bold text-gray-800">
                            <span className="font-mono text-xs text-gray-400 mr-2 bg-gray-100 px-1.5 py-0.5 rounded">
                              {log.method || 'GET'}
                            </span>
                            {log.action_description}
                          </td>
                          <td className="px-6 py-4 font-mono text-xs text-gray-600 font-semibold">{log.ip_address || '—'}</td>
                          <td className="px-6 py-4">
                            <span className={`text-[10px] font-black px-2 py-0.5 rounded ${log.status_code < 400 ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
                              {log.status_code || '200 OK'}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div>
              <div className="p-6 border-b border-gray-50 bg-gray-50/50">
                <h3 className="font-black text-xs text-gray-400 uppercase tracking-widest">Журнал авторизаций и активных сессий</h3>
              </div>
              <div className="overflow-auto max-h-[600px]">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-gray-50/70 text-gray-400 text-[10px] font-black uppercase tracking-wider border-b border-gray-100">
                      <th className="px-6 py-4">Вход в систему</th>
                      <th className="px-6 py-4">Используемый IP</th>
                      <th className="px-6 py-4">Устройство / Браузер</th>
                      <th className="px-6 py-4">Окончание сессии</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {auditData.sessions.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="p-10 text-center text-sm font-medium text-gray-400">История сессий пуста</td>
                      </tr>
                    ) : (
                      auditData.sessions.map((session, idx) => (
                        <tr key={idx} className="hover:bg-gray-50/50 transition-colors text-sm">
                          <td className="px-6 py-4 text-gray-800 font-bold">
                            {new Date(session.login_time).toLocaleString()}
                          </td>
                          <td className="px-6 py-4 font-mono text-xs text-blue-600 font-bold">
                            <span className="bg-blue-50 border border-blue-100 px-2.5 py-1 rounded-lg">
                              {session.ip_address}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-xs text-gray-500 font-medium max-w-xs truncate" title={session.user_agent}>
                            {session.user_agent || 'Неизвестное устройство'}
                          </td>
                          <td className="px-6 py-4 text-gray-400">
                            {session.logout_time ? new Date(session.logout_time).toLocaleString() : (
                              <span className="flex items-center text-green-600 font-bold text-xs uppercase tracking-wider">
                                <span className="h-2 w-2 rounded-full bg-green-500 mr-2 animate-ping"></span>
                                Активна сейчас
                              </span>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default UserActivityDetail;